import * as Sentry from "@sentry/react";
import { RecruiterJobMetric, Job as OldJobApi } from "../resources";
import Recruiter from "../apiResources/Recruiter";
import Employer from "../apiResources/Employer";
import Engagement from "../apiResources/Engagement";
import Job from "../apiResources/Job";
import Placement from "../apiResources/Placement";
import YearlyHhTimeToHire from "../apiResources/YearlyHhTimeToHire";
import {
  LOAD_AGENCY_METRICS,
  LOAD_AGENCY_METRICS_ERROR,
  LOAD_EMPLOYER_METRICS,
  LOAD_EMPLOYER_METRICS_ERROR,
  TOGGLE_FINISH_JOB_POST_MODAL,
} from "./types";
import { sumEstimatedPlacementFees } from "./adapters/dashboardAdapters/agencyMetricsAdapter";

export const loadAgencyMetrics =
  ({ recruiterId }) =>
  async (dispatch) => {
    try {
      const recruiterJobMetric = await RecruiterJobMetric.all();
      const engagements = await Engagement.where({
        status: "accepted",
        job_status: ["new", "open", "interview_stage", "offer_made"],
      })
        .includes("job")
        .stats({ total: "count" })
        .all();
      const engagementsWithApplicationsAtInterview = await Engagement.where({
        status: "accepted",
        application_at_interview_stage: true,
      })
        .includes(["job", "applications"])
        .all();

      const recruiter = await Recruiter.includes([
        "net_recruiter_placement_fee",
        "currency",
      ]).find(recruiterId);

      const data = {
        performance: {
          memberSince: recruiterJobMetric.memberSince,
          interviewRate: recruiterJobMetric.applicationsToInterviewPercent,
          placementRate: recruiterJobMetric.applicationsToPlacementPercent,
          engagements: recruiterJobMetric.totalJobs,
          submissions: recruiterJobMetric.applicationsSubmitted,
          interviews: recruiterJobMetric.interviewsConfirmed,
          placements: recruiterJobMetric.placementsMade,
          averageCVs: recruiterJobMetric.averageApplicationsPerRole,
        },
        pipeline: {
          interviews: sumEstimatedPlacementFees(
            engagementsWithApplicationsAtInterview.data,
            recruiter.data
          ),
          interviewsCount: engagementsWithApplicationsAtInterview.data.length,
          engagements: sumEstimatedPlacementFees(
            engagements.data,
            recruiter.data
          ),
          engagementsCount: engagements.meta.stats.total.count,
          currency: recruiter.data.currency,
        },
      };

      dispatch({
        type: LOAD_AGENCY_METRICS,
        payload: {
          data,
          recruiter: recruiter.data,
        },
      });
    } catch (error) {
      Sentry.captureException(error);

      dispatch({
        type: LOAD_AGENCY_METRICS_ERROR,
      });
    }
  };

export const loadEmployerMetrics =
  ({ companyId }) =>
  async (dispatch) => {
    try {
      const avgTimeToHireJobStats = await Job.per(0)
        .stats({ time_to_hire_last_year: "custom_stat" })
        .all()
        .then((response) => response.meta.stats.time_to_hire_last_year);

      const liveJobs = await Job.where({ is_live: true, deleted_at: null })
        .per(0)
        .stats({ total_number_of_positions: "custom_stat" })
        .all()
        .then((response) => response.meta.stats.total_number_of_positions);

      const employer = await Employer.find(companyId);

      const placementCount = await Placement.where({ status: "successful" })
        .per(0)
        .stats({ total: "count" })
        .all();

      const avgTimeToHire = avgTimeToHireJobStats.custom_stat;

      const hhTimeToHire = await YearlyHhTimeToHire.all();

      dispatch({
        type: LOAD_EMPLOYER_METRICS,
        payload: {
          isPortalEmployer: employer.data.portal,
          performance: {
            averageTimeToRespond: employer.data.averageTimeToRespond,
            hhAverageTimeToRespond: employer.data.hhAverageTimeToRespond,
            memberSince: employer.data.memberSince,
          },
          timeToHire: {
            yourAvgTimeToHire: avgTimeToHire < 1 ? 1 : avgTimeToHire,
            hhAvgTimeToHire: `${hhTimeToHire.data[0].lastTwelveMonths} days`,
            industryAvgTimeToHire: "44 days",
          },
          statistics: {
            liveJobs: liveJobs.custom_stat,
            placements: placementCount.meta.stats.total.count,
          },
          customTerms: employer.data.customTerms,
          name: employer.data.name,
          accountManager: employer.data.accountManager,
        },
      });
    } catch (error) {
      Sentry.captureException(error);

      dispatch({
        type: LOAD_EMPLOYER_METRICS_ERROR,
      });
    }
  };

export const toggleFinishJobPostModal = (jobId) => async (dispatch) => {
  try {
    const jobToFinish = await OldJobApi.find(jobId);

    dispatch({
      type: TOGGLE_FINISH_JOB_POST_MODAL,
      payload: jobToFinish,
    });
    document.body.classList.toggle("modalOverlay");
  } catch (error) {
    Sentry.captureException(error);
  }
};

export { loadAgencyMetrics as default };
